import React, { useEffect, useRef, useState } from 'react';
import styles from './TwoFactorAuthentication.module.scss';
import BreadCrumb from '@ds/elements/BreadCrumb/BreadCrumb';
import { Switch } from '@ds/elements/Switch/Switch';
import ArrowRight from '@icons/arrow-right.svg';
import { TagVariant } from '../../../../design-system/elements/Tag/Tag.types';
import { Tag, Toast } from '@ds';
import { useGetDeviceRegistrationDate } from 'src/shared/queries/useGetDeviceRegistrationDate';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';
import { useSaveUserMFASettings } from 'src/shared/mutations/useSaveUserMFASettings';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { useRemoveMFARegisteredDevice } from 'src/shared/mutations/useRemoveMFARegisteredDevice';
import NewDeviceModal from './ConfirmationModal/NewDeviceModal';
import GenerateKeyModal from './ConfirmationModal/GenerateKeyModal';

const TwoFactorAuth: React.FC = () => {
  const { data: user } = useGetUser();
  const { data: deviceRegistration } = useGetDeviceRegistrationDate(user?.userId || 0);
  const { data: linkedUserDeviceRegistration } = useGetDeviceRegistrationDate(user?.linkedUsers?.[0]?.userId || 0);
  const { mutate: saveMFASettings } = useSaveUserMFASettings();
  const { mutate: removeMFARegisteredDevice } = useRemoveMFARegisteredDevice();
  const toastRef = useRef<IToastr>(null);

  const [isDeviceRegistrationFromLinkedUser, setIsDeviceRegistrationFromLinkedUser] = useState(false);
  const [isEnabled, setIsEnabled] = useState(user?.isMFAEnabled ?? false);
  const [isNewDeviceModalOpen, setIsNewDeviceModalOpen] = useState(false);
  const [isGenerateKeyModalOpen, setIsGenerateKeyModalOpen] = useState(false);

  useEffect(() => {
    if (deviceRegistration == null && linkedUserDeviceRegistration) {
      setIsDeviceRegistrationFromLinkedUser(true);
    }
  }, [deviceRegistration, linkedUserDeviceRegistration]);

  const breadcrumbItems = [
    { label: 'My Preferences', url: '/user-preferences' },
    { label: 'Two Factor Authentication', url: '/user-preferences/two-factor' },
  ];

  const handleMFAEnabledChange = (value: boolean) => {
    setIsEnabled(value);
    Promise.all([
      saveMFASettings(
        { isMFAEnabled: value, userId: user?.userId || 0 },
        {
          onSuccess: () => {
            setIsEnabled(value);
            showToast('Settings saved successfully.', 'success');
          },
          onError: (error) => {
            setIsEnabled(!value);
            console.error(error);
            showToast('Error saving settings. Try again later.', 'error');
          },
        }
      ),
      removeMFARegisteredDevice(),
    ]).catch((error) => {
      console.error('Error during combined operations:', error);
      showToast('An error occurred while processing your request. Try again later.', 'error');
    });
  };

  const showGenerateKeyModal = () => {
    if (isEnabled) {
      setIsGenerateKeyModalOpen(true);
    }
  };

  const hideGenerateKeyModal = () => {
    setIsGenerateKeyModalOpen(false);
  };

  const showNewDeviceModal = () => {
    if (isEnabled) {
      setIsNewDeviceModalOpen(true);
    }
  };

  const hideNewDeviceModal = () => {
    setIsNewDeviceModalOpen(false);
  };

  const showToast = async (message: string, severity: 'success' | 'error') => {
    toastRef.current?.show({ message, severity });
  };

  return (
    <div className={`${styles['two-factor-auth']}`}>
      <div className="flex flex-col flex-start justify-between">
        <BreadCrumb items={breadcrumbItems} />
        <div className="flex flex-row justify-between">
          <h2>Two Factor Authentication</h2>
          <Tag
            className={`min-h-[28px] inline-block sm:hidden self-start`}
            variant={isEnabled ? TagVariant.Success : TagVariant.Info}
          >
            {isEnabled ? 'Enabled' : 'Disabled'}
          </Tag>
        </div>
      </div>
      <div className={`${styles['two-factor-auth__content']}`}>
        <div className={`mt-4 px-0 sm:px-4 flex align-start gap-4 ${styles['enable-2fa']}`}>
          <p>For extra security, enable two-factor authentication</p>
          <div className={`flex align-start gap-2`}>
            <Switch className="switch-2fa" checked={isEnabled} onChange={handleMFAEnabledChange} />
            <Tag
              className={`min-h-[28px] hidden sm:inline-block`}
              variant={isEnabled ? TagVariant.Success : TagVariant.Info}
            >
              {isEnabled ? 'Enabled' : 'Disabled'}
            </Tag>
          </div>
        </div>

        <div className={`mt-6 ${styles['actions-2fa']} ${isEnabled ? '' : styles['disabled']}`}>
          <div className={`mt-2 py-2 px-0 sm:px-4 ${styles['new-device']}`}>
            <h5 className="py-2 flex align-start gap-2" onClick={showNewDeviceModal}>
              setup new device <img src={ArrowRight} className="w-5" />
            </h5>

            <p>
              Your new device will work right away and all and the existing one will no longer work.
              {(deviceRegistration?.updatedDateOnly || linkedUserDeviceRegistration?.updatedDateOnly) && (
                <>
                  <br />
                  <span>
                    {' '}
                    Last device enabled since{' '}
                    {isDeviceRegistrationFromLinkedUser
                      ? getFormattedDate(linkedUserDeviceRegistration?.updatedDateOnly as string, UserDateFormat.get())
                      : getFormattedDate(deviceRegistration?.updatedDateOnly as string, UserDateFormat.get())}
                    .
                  </span>
                </>
              )}
            </p>
          </div>
          <div className={`mt-2 py-2 px-0 sm:px-4 ${styles['new-recovery-key']}`}>
            <h5 className="py-2 flex align-start gap-2" onClick={showGenerateKeyModal}>
              Generate new recovery key <img src={ArrowRight} className="w-5" />
            </h5>
            <p>Your new recovery key will be valid right away and the existing one will no longer work.</p>
          </div>
        </div>
        <hr className="hidden sm:block w-full my-4" />

        <div className={`mt-2 py-2 px-0 sm:px-8 ${styles['note-2fa']} ${isEnabled ? '' : styles['disabled']}`}>
          <h4 className="mb-4">What you need to know</h4>
          <p>
            We'll prompt you for a verification code when you log in to your Cobalt applications. You'll get the code
            from the verification app on your smartphone.
            <br />
            We won't prompt you with two-factor authentication if you log in with single sign-on. Please contact your
            system adminstrator to find out more about this option.
          </p>
        </div>
      </div>

      <Toast ref={toastRef} />

      <NewDeviceModal visible={isNewDeviceModalOpen} hideNewDeviceModal={hideNewDeviceModal} />

      <GenerateKeyModal
        visible={isGenerateKeyModalOpen}
        hideGenerateKeyModal={hideGenerateKeyModal}
        showToast={showToast}
      />
    </div>
  );
};

export default TwoFactorAuth;
